import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useHistory} from "react-router-dom";
import {CButton, CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle, CSelect} from '@coreui/react';
import CIcon from "@coreui/icons-react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import {DatesSetArg, EventInput} from '@fullcalendar/common';
import {getApi} from "../../apis/backendApis";
import useAlert from "../../services/useAlert";
import useLoading from "../../services/useLoading";
import {AgendaGeral, Evento, PrazoAgenda, Processo, SituacaoEvento, TIPOS_EVENTO, dataBr, dataCurta, dataHoraApi, erroDaApi, isoLocal, quandoEvento} from "./modelo";
import Calendario, {COR_EVENTO, COR_PRAZO, COR_PRAZO_ATRASADO} from "./calendario";
import EventoModal from "./eventoModal";
import Remarcacao, {PedidoRemarcacao, pedidoDoCalendario} from "./remarcacao";
import {NovoAgendamento} from "./agendaAba";
import '../../scss/styles-processo.scss';

const formata = (d: Date) => dataHoraApi(d).slice(0, 10);

interface OpcaoProcesso {
    value: number,
    label: string,
}

/**
 * Cronograma gerencial (#480058, passo 8): eventos e prazos de todos os
 * processos em aberto, com vencidos, vencendo, carga por responsavel e marcos.
 *
 * E tambem onde se agenda (#484909): "Novo evento" ou selecionar um horario
 * pede o processo (e, no modal, a etapa e o item); clicar no evento abre o
 * modal; arrastar remarca o evento ou reprograma o prazo do item. O backend
 * so deixa quem participa do processo.
 */
const CronogramaGeral = () => {
    const history = useHistory();
    const [showAlert] = useAlert();
    const [setLoading] = useLoading();
    const [periodo, setPeriodo] = useState<{inicio: string, fim: string} | null>(null);
    const [gestor, setGestor] = useState('');
    const [responsavel, setResponsavel] = useState('');
    const [cliente, setCliente] = useState<{value: number, label: string} | null>(null);
    const [tipo, setTipo] = useState('');
    const [comPrazos, setComPrazos] = useState(false);
    const [dados, setDados] = useState<AgendaGeral | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [abertos, setAbertos] = useState<Set<string>>(new Set());
    const [versao, setVersao] = useState(0);
    const [usuarios, setUsuarios] = useState<Array<{id: number, nome: string}>>([]);
    // Novo evento: primeiro o processo, depois o modal do evento.
    const [escolhendo, setEscolhendo] = useState<NovoAgendamento | null>(null);
    const [processoEscolhido, setProcessoEscolhido] = useState<OpcaoProcesso | null>(null);
    const [modal, setModal] = useState<({processo: Processo, evento: Evento | null} & NovoAgendamento) | null>(null);
    const [remarcando, setRemarcando] = useState<PedidoRemarcacao | null>(null);
    const requisicao = useRef(0);

    useEffect(() => {
        if (!periodo) return;
        const minha = ++requisicao.current;
        getApi().get('processo/agenda', {
            params: {
                inicio: periodo.inicio,
                fim: periodo.fim,
                gestor_id: gestor || undefined,
                usuario_id: responsavel || undefined,
                cliente_id: cliente?.value,
            },
        }).then(resposta => {
            if (minha === requisicao.current) {
                setDados(resposta.data);
                setErro(null);
            }
        }).catch(e => setErro(String(e?.response?.data?.error ?? 'Não foi possível carregar a agenda.')));
    }, [periodo, gestor, responsavel, cliente, versao]);

    useEffect(() => {
        getApi().get('/usuarios/suggest').then(resposta => {
            setUsuarios((resposta.data.usuarios || []).map((par: any) => {
                const [id, nome] = Object.entries(par)[0];
                return {id: Number(id), nome: String(nome)};
            }));
        }).catch(() => setUsuarios([]));
    }, []);

    // datesSet dispara a cada render do calendario: so muda o periodo se mudou de fato.
    const mudaPeriodo = useCallback((arg: DatesSetArg) => {
        const fim = new Date(arg.end.getTime() - 86400000);
        const novo = {inicio: formata(arg.start), fim: formata(fim)};
        setPeriodo(atual => (atual && atual.inicio === novo.inicio && atual.fim === novo.fim ? atual : novo));
    }, []);

    const noCalendario: EventInput[] = useMemo(() => {
        if (!dados) return [];
        const lista: EventInput[] = dados.eventos.filter(evento => !tipo || evento.tipo === Number(tipo)).map(evento => {
            const agendado = evento.situacao === SituacaoEvento.AGENDADO;
            return {
                id: `e${evento.id}`,
                title: `${evento.cliente}: ${evento.titulo}`,
                start: isoLocal(evento.inicio),
                end: evento.fim ? isoLocal(evento.fim) : undefined,
                backgroundColor: evento.situacao === SituacaoEvento.CANCELADO ? '#e5e7eb' : COR_EVENTO[evento.tipo],
                borderColor: evento.situacao === SituacaoEvento.CANCELADO ? '#e5e7eb' : COR_EVENTO[evento.tipo],
                textColor: evento.situacao === SituacaoEvento.CANCELADO ? '#6b7280' : '#fff',
                classNames: [`sit-evento-${evento.situacao}`],
                startEditable: agendado,
                durationEditable: agendado,
                extendedProps: {evento, processoId: evento.processo_id},
            };
        });
        if (comPrazos && !tipo) {
            dados.prazos.forEach(prazo => lista.push({
                id: `i${prazo.item_id}`,
                title: `${prazo.agendado ? '📅 ' : ''}${prazo.cliente}: ${prazo.titulo}`,
                start: prazo.prazo,
                allDay: true,
                backgroundColor: 'transparent',
                borderColor: prazo.atrasado ? COR_PRAZO_ATRASADO : COR_PRAZO,
                textColor: prazo.atrasado ? COR_PRAZO_ATRASADO : '#475569',
                classNames: ['pr-cal-prazo'],
                startEditable: true,
                durationEditable: false,
                extendedProps: {itemId: prazo.item_id, titulo: prazo.titulo, processoId: prazo.processo_id},
            }));
        }
        return lista;
    }, [dados, comPrazos, tipo]);

    const irPara = (processoId: number, pedido = '') => history.push({pathname: `/processos/${processoId}`, search: pedido, state: {voltar: '/processos/cronograma'}});

    /** Abre o modal do evento com o processo dele (o modal precisa das etapas, itens e papeis). */
    const abreEvento = (evento: Evento) => {
        if (!evento.processo_id) return;
        setLoading(true);
        getApi().get(`processo/get/${evento.processo_id}`)
            .then(r => setModal({processo: r.data, evento: (r.data as Processo).eventos.find(e => e.id === evento.id) ?? evento}))
            .catch(e => showAlert({title: 'Não foi possível', msg: erroDaApi(e, 'Erro ao abrir o evento.'), type: 'danger', toaster: true}))
            .finally(() => setLoading(false));
    };

    const escolheProcesso = () => {
        if (!processoEscolhido || !escolhendo) return;
        setLoading(true);
        getApi().get(`processo/get/${processoEscolhido.value}`)
            .then(r => {
                setModal({processo: r.data, evento: null, ...escolhendo});
                setEscolhendo(null);
            })
            .catch(e => showAlert({title: 'Não foi possível', msg: erroDaApi(e, 'Erro ao abrir o processo.'), type: 'danger', toaster: true}))
            .finally(() => setLoading(false));
    };

    const buscaProcessos = (busca: string) => getApi().get('processo/agenda/processos', {params: {busca}})
        .then(r => (r.data as Array<{id: number, cliente: string, fluxo: string, titulo: string}>)
            .map(p => ({value: p.id, label: `${p.cliente} · ${p.fluxo} · ${p.titulo}`})))
        .catch(() => []);

    const alterna = (chave: string) => {
        const novos = new Set(abertos);
        novos.has(chave) ? novos.delete(chave) : novos.add(chave);
        setAbertos(novos);
    };
    // Opcoes dos filtros vem com a agenda: so quem tem algo no periodo.
    const opcoes = dados?.filtros;
    const clientes = useMemo(() => (opcoes?.clientes || []).map(c => ({value: c.id, label: c.nome})), [opcoes]);

    const renderItens = (itens: PrazoAgenda[]) => itens.map(item => (
        <button type="button" key={item.item_id} className="pr-geral-item" onClick={() => irPara(item.processo_id, `?item=${item.item_id}`)}>
            <span className={item.atrasado ? 'ruim' : ''}>{dataCurta(item.prazo)}</span>
            <span>
                <strong>{item.cliente}</strong> · {item.titulo}
                {item.agendado && <em className="pr-geral-agendado"><CIcon name="cil-calendar"/> {quandoEvento(item.agendado)}</em>}
            </span>
        </button>
    ));

    const maiorCarga = Math.max(1, ...(dados?.carga || []).map(c => c.total));

    return (
        <div className="pr-geral">
            <header className="pr-geral-topo">
                <div>
                    <h4>Cronograma dos processos</h4>
                    <span>Eventos e prazos de todos os processos em aberto · arraste para remarcar</span>
                </div>
                <div className="pr-geral-filtros">
                    <CSelect value={gestor} onChange={(e: any) => setGestor(e.target.value)} title="Gestor do processo">
                        <option value="">Todos os gestores</option>
                        {opcoes?.gestores.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                    </CSelect>
                    <CSelect value={responsavel} onChange={(e: any) => setResponsavel(e.target.value)} title="Responsável pelo item ou participante do evento">
                        <option value="">Todos os responsáveis</option>
                        {opcoes?.responsaveis.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                    </CSelect>
                    <div className="pr-geral-cliente">
                        <Select options={clientes} value={cliente} isClearable placeholder="Todos os clientes"
                                noOptionsMessage={() => 'Nenhum cliente com registros no período'}
                                onChange={(valor: any) => setCliente(valor)} classNamePrefix="pr-select"/>
                    </div>
                    <CSelect value={tipo} onChange={(e: any) => setTipo(e.target.value)} title="Tipo de evento (escolhido, os prazos dos itens saem do calendário)">
                        <option value="">Todos os tipos</option>
                        {TIPOS_EVENTO.getList().map((t: any) => <option key={t.value} value={t.value}>{t.description}</option>)}
                    </CSelect>
                    <label className="pr-opcao pequena">
                        <input type="checkbox" checked={comPrazos} onChange={e => setComPrazos(e.target.checked)}/> prazos dos itens
                    </label>
                    <CButton color="primary" size="sm" className="pr-geral-novo" onClick={() => { setProcessoEscolhido(null); setEscolhendo({}); }}>
                        <CIcon name="cil-plus"/> Novo evento
                    </CButton>
                </div>
            </header>
            {erro && <div className="pr-aviso">{erro}</div>}
            <div className="pr-geral-corpo">
                <section className="pr-geral-calendario">
                    <Calendario eventos={noCalendario} aoMudarPeriodo={mudaPeriodo}
                                aoClicar={arg => {
                                    const {evento, itemId, processoId} = arg.event.extendedProps as any;
                                    if (evento) abreEvento(evento);
                                    else if (itemId) irPara(processoId, `?item=${itemId}`);
                                }}
                                aoSelecionar={arg => {
                                    arg.view.calendar.unselect();
                                    setProcessoEscolhido(null);
                                    setEscolhendo(arg.allDay ? {data: formata(arg.start)} : {inicio: dataHoraApi(arg.start), fim: dataHoraApi(arg.end)});
                                }}
                                aoMover={arg => {
                                    const pedido = pedidoDoCalendario(arg.event, arg.oldEvent, arg.revert);
                                    pedido ? setRemarcando(pedido) : arg.revert();
                                }}
                                aoRedimensionar={arg => {
                                    const pedido = pedidoDoCalendario(arg.event, arg.oldEvent, arg.revert);
                                    pedido ? setRemarcando(pedido) : arg.revert();
                                }}/>
                </section>
                <aside className="pr-geral-lado">
                    <section className="pr-bloco">
                        <div className="pr-bloco-titulo">Vencidos · {dados?.vencidos.reduce((s, g) => s + g.total, 0) ?? 0}</div>
                        {dados?.vencidos.length === 0 && <p className="pr-ajuda">Nada vencido.</p>}
                        {dados?.vencidos.map(grupo => (
                            <div key={grupo.responsavel} className="pr-geral-grupo">
                                <button type="button" className="pr-geral-pessoa" onClick={() => alterna(`v-${grupo.responsavel}`)}>
                                    <span>{grupo.responsavel}</span><span className="pr-chip atrasado">{grupo.total}</span>
                                </button>
                                {abertos.has(`v-${grupo.responsavel}`) && renderItens(grupo.itens)}
                            </div>
                        ))}
                    </section>
                    <section className="pr-bloco">
                        <div className="pr-bloco-titulo">Vencendo em até 2 dias úteis · {dados?.vencendo.reduce((s, g) => s + g.total, 0) ?? 0}</div>
                        {dados?.vencendo.length === 0 && <p className="pr-ajuda">Nada vencendo.</p>}
                        {dados?.vencendo.map(grupo => (
                            <div key={grupo.responsavel} className="pr-geral-grupo">
                                <button type="button" className="pr-geral-pessoa" onClick={() => alterna(`p-${grupo.responsavel}`)}>
                                    <span>{grupo.responsavel}</span><span className="pr-chip vencendo">{grupo.total}</span>
                                </button>
                                {abertos.has(`p-${grupo.responsavel}`) && renderItens(grupo.itens)}
                            </div>
                        ))}
                    </section>
                    <section className="pr-bloco">
                        <div className="pr-bloco-titulo">Carga nos próximos {dados?.janela_carga ?? 10} dias úteis</div>
                        {dados?.carga.length === 0 && <p className="pr-ajuda">Sem prazos no período.</p>}
                        {dados?.carga.slice(0, 12).map(linha => (
                            <div key={linha.responsavel} className="pr-geral-carga">
                                <span>{linha.responsavel}</span>
                                <span className="pr-barra"><span style={{width: `${linha.total * 100 / maiorCarga}%`}}/></span>
                                <strong>{linha.total}</strong>
                            </div>
                        ))}
                    </section>
                    <section className="pr-bloco">
                        <div className="pr-bloco-titulo">Próximos marcos no período</div>
                        {dados?.marcos.length === 0 && <p className="pr-ajuda">Nenhum kick-off, treinamento, entrega ou go-live agendado.</p>}
                        {dados?.marcos.map(marco => (
                            <button type="button" key={marco.id} className="pr-agenda-evento" onClick={() => abreEvento(marco)}>
                                <span className="pr-agenda-cor" style={{background: COR_EVENTO[marco.tipo]}}/>
                                <span>
                                    <strong>{marco.cliente}</strong>
                                    <em>{marco.tipo_nome} · {dataBr(marco.inicio)}</em>
                                    {marco.item_titulo && <em className="pr-agenda-item"><CIcon name="cil-task"/> {marco.item_titulo}</em>}
                                </span>
                            </button>
                        ))}
                    </section>
                </aside>
            </div>

            {escolhendo && (
                <CModal show onClose={() => setEscolhendo(null)} centered>
                    <CModalHeader closeButton>
                        <CModalTitle>Novo evento</CModalTitle>
                    </CModalHeader>
                    <CModalBody>
                        {(escolhendo.inicio || escolhendo.data) && (
                            <p className="pr-ajuda">
                                {escolhendo.inicio ? `${quandoEvento(escolhendo.inicio)}${escolhendo.fim ? `–${escolhendo.fim.slice(11, 16)}` : ''}` : dataBr(escolhendo.data)}
                            </p>
                        )}
                        <label className="pr-geral-escolha">
                            <span>Processo</span>
                            <AsyncSelect cacheOptions defaultOptions loadOptions={buscaProcessos} value={processoEscolhido}
                                         onChange={(valor: any) => setProcessoEscolhido(valor)} placeholder="Cliente, título ou fluxo"
                                         noOptionsMessage={() => 'Nenhum processo em aberto em que você participa'}
                                         loadingMessage={() => 'Buscando…'} classNamePrefix="pr-select" aria-label="Processo"/>
                        </label>
                        <p className="pr-ajuda">A etapa e o item da checklist você escolhe no próximo passo.</p>
                    </CModalBody>
                    <CModalFooter>
                        <CButton color="dark" variant="outline" onClick={() => setEscolhendo(null)}>Cancelar</CButton>
                        <CButton color="primary" disabled={!processoEscolhido} onClick={escolheProcesso}>Continuar</CButton>
                    </CModalFooter>
                </CModal>
            )}
            {modal && (
                <EventoModal key={modal.evento?.id ?? 'novo'} processo={modal.processo} evento={modal.evento} usuarios={usuarios}
                             dataInicial={modal.data} inicioInicial={modal.inicio} fimInicial={modal.fim}
                             onFechar={() => setModal(null)} aoSalvar={() => setVersao(v => v + 1)}
                             onAbrirItem={itemId => irPara(modal.processo.id, `?item=${itemId}`)}
                             onAbrirProcesso={() => irPara(modal.processo.id, modal.evento ? `?evento=${modal.evento.id}` : '')}/>
            )}
            {remarcando && <Remarcacao pedido={remarcando} aoFechar={feito => { setRemarcando(null); if (feito) setVersao(v => v + 1); }}/>}
        </div>
    );
};

export default CronogramaGeral;
